import { backendApi } from "@/services/backend/api";
import {
  Certificate,
  CreatedResponse,
  CreateRequest,
  CustomerCertificateAddRequest,
  CustomerCertificateListRequest,
  CustomerCertificateListResponse,
  CustomerCertificateRemoveRequest,
  DeleteRequest,
  EmptyResponse,
  GetRequest,
  ListRequest,
  ListResponse,
  UpdateRequest,
} from "@/services/backend/qpm/certificates/types/certificate";

const qpmCertificateHttpApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["QpmCertificate", "QpmCustomerCertificate"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      qpmCertificateHttpCreate: build.mutation<CreatedResponse, CreateRequest>({
        query: (request) => ({
          url: `/qpm/certificates`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "QpmCertificate", id: "LIST" }],
      }),

      qpmCertificateHttpGet: build.query<Certificate, GetRequest>({
        query: (request) => ({
          url: `/qpm/certificates/get`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result
            ? [{ type: "QpmCertificate", id: result.id }]
            : [{ type: "QpmCertificate", id: "LIST" }],
      }),

      qpmCertificateHttpList: build.query<ListResponse, ListRequest>({
        query: (request) => ({
          url: `/qpm/certificates/list`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "QpmCertificate", id: "LIST" },
                ...result.items.map((it) => ({
                  type: "QpmCertificate" as const,
                  id: it.id,
                })),
              ]
            : [{ type: "QpmCertificate", id: "LIST" }],
      }),

      qpmCertificateHttpUpdate: build.mutation<EmptyResponse, UpdateRequest>({
        query: (request) => ({
          url: `/qpm/certificates`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "QpmCertificate", id: "LIST" },
          { type: "QpmCertificate", id: arg.id },
        ],
      }),

      qpmCertificateHttpDelete: build.mutation<EmptyResponse, DeleteRequest>({
        query: (request) => ({
          url: `/qpm/certificates`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: "QpmCertificate", id: "LIST" },
          { type: "QpmCertificate", id: arg.id },
        ],
      }),

      // Customer certificates
      qpmCertificateCustomerAdd: build.mutation<
        EmptyResponse,
        CustomerCertificateAddRequest
      >({
        query: (request) => ({
          url: `/qpm/certificates/customer/add`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "QpmCustomerCertificate", id: "LIST" }],
      }),

      qpmCertificateCustomerRemove: build.mutation<
        EmptyResponse,
        CustomerCertificateRemoveRequest
      >({
        query: (request) => ({
          url: `/qpm/certificates/customer/remove`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "QpmCustomerCertificate", id: "LIST" }],
      }),

      qpmCertificateCustomerList: build.query<
        CustomerCertificateListResponse,
        CustomerCertificateListRequest
      >({
        query: (request) => ({
          url: `/qpm/certificates/customer/list`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "QpmCustomerCertificate", id: "LIST" },
                ...result.items.map((it) => ({
                  type: "QpmCustomerCertificate" as const,
                  id: it.id,
                })),
              ]
            : [{ type: "QpmCustomerCertificate", id: "LIST" }],
      }),
    }),
  });

export const {
  useQpmCertificateHttpCreateMutation,
  useQpmCertificateHttpGetQuery,
  useQpmCertificateHttpListQuery,
  useQpmCertificateHttpUpdateMutation,
  useQpmCertificateHttpDeleteMutation,
  useQpmCertificateCustomerAddMutation,
  useQpmCertificateCustomerRemoveMutation,
  useQpmCertificateCustomerListQuery,
} = qpmCertificateHttpApi;
